import { AfterViewInit, Component, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';

import { CropFieldAdvice, Treatment, TreatmentType } from '@next/arable-lib/advice';
import { getCrops, RawCrop } from '@next/arable-lib/crop';
import { loadGenericCropAids, loadProprietaryCropAids } from '@next/arable-lib/crop-aid';
import { CropField, getArableAdviceCropFields, loadArableAdviceCropFields } from '@next/arable-lib/crop-field';
import { CropFieldType } from '@next/arable-lib/shared';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { CartActions, CartSelectors } from '@next/core-lib/cart';
import { DropdownButtonComponent, SummaryCardAction, SummaryCardComponent } from '@next/core-lib/components';
import { DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { TableColumnTypes, TableComponent, TableConfig, TableStickyTypes } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { combineLatest, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { OrderLineStatus, TreatmentTypes } from 'src/app/shared/models';

import { NexusAIIdentifiers } from '../../../dashboard/shared/constants';
import {
  CICHOREI_AGRIFIRM_CROP_ID,
  CICHOREI_WARNING_EDICROP_IDS,
  CORN_AGRIFIRM_CROP_IDS,
  CropFieldWrapper,
  SUIKERBIET_AGRIFIRM_CROP_ID,
  SUIKERBIET_WARNING_EDICROP_IDS,
} from '../../models/models';

import { AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  editCustomerProductStock,
  loadAutomaticAdvice,
  loadCompanySettings,
  loadCropFieldAdvices,
  loadCustomerOrderPlan,
  loadFertilizerOrderLines,
  loadFertilizerOrderPlan,
  loadGBMOrderLines,
  openFertilizerProposalDialog,
} from '../../store/actions';
import {
  selectCompanySettings,
  selectCropFieldAdvices,
  selectFertilizerOrderPlan,
  selectGBMOrderLines,
  selectIsUserEligibleToViewCropFieldAdvices,
} from '../../store/selectors';
import { selectCropYear } from '../../../store/selectors';
import { CropYearService } from '../../../shared/services/crop-year.service';
import { CropFieldDialogComponent } from '../cropfield-dialog/cropfield-dialog.component';
import { GreenManureComponent } from '../green-manure/green-manure.component';
import { GreenManureData } from '../green-manure/green-manure.model';
import { Environment } from '../../../../environments/environment.model';

@Component({
  selector: 'app-widget-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  imports: [
    SummaryCardComponent,
    DropdownButtonComponent,
    MatButton,
    MatIcon,
    MatTooltip,
    TableComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ListComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly cropYearService = inject(CropYearService);
  protected readonly isCropYearReadOnly = this.cropYearService.isReadOnly;
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly router = inject(Router);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  protected readonly environment = inject<Environment>(EnvironmentInjector);

  readonly greenManureOpen = signal(false);
  private readonly greenManurePermission = AuthorizationSelectors.hasAtLeastAdvisorRoleForApplicationKey('My Arable');
  readonly canOpenGreenManure = this.store.selectSignal(this.greenManurePermission);
  private readonly materialDialog = inject(MatDialog);
  private readonly selectedCustomer = this.store.selectSignal(UserSelectors.selectCustomer);

  @ViewChild('treatmentCell') treatmentCellTemplate!: TemplateRef<unknown>;
  @ViewChild('greenManureAction') greenManureActionTemplate!: TemplateRef<unknown>;
  private readonly destroy$ = new Subject<void>();
  readonly boundOpenOrderGBMPopup = this.orderGbm.bind(this);
  readonly boundOpenOrderFertilizersPopup = this.orderFertilizers.bind(this);
  readonly boundOpenShoppingCartAction = this.navigateShoppingCart.bind(this);

  cropFieldsTableConfig = signal<TableConfig | null>(null);
  companySettings$ = this.store.select(selectCompanySettings);
  isUserEligibleToViewCropFieldAdvices$ = this.store.select(selectIsUserEligibleToViewCropFieldAdvices);

  hasCropFields$ = this.store.pipe(
    select(getArableAdviceCropFields),
    map((advices) => advices.length > 0),
  );

  hasOpenGbmOrders$ = this.store.pipe(
    select(selectGBMOrderLines),
    map((orderLines) =>
      orderLines.some(
        (orderLine) =>
          orderLine.status === OrderLineStatus.open &&
          orderLine.treatmentType === TreatmentTypes.GBM &&
          orderLine.count > 0,
      ),
    ),
  );

  hasFertilizerProducts$ = this.store.pipe(
    select(selectFertilizerOrderPlan),
    map((orderPlan) => {
      if (!orderPlan) return false;
      const proprietaryFertilizers = (orderPlan as { proprietaryFertilizers?: unknown[] })?.proprietaryFertilizers;
      const proprietaryCount = Array.isArray(proprietaryFertilizers) ? proprietaryFertilizers.length : 0;
      return (
        (orderPlan.orderableProducts?.length ?? 0) +
        (orderPlan.nonOrderableProducts?.length ?? 0) +
        proprietaryCount >
        0
      );
    }),
  );

  hasFertilizerPlanLoaded$ = this.store.pipe(
    select(selectFertilizerOrderPlan),
    map((orderPlan) => !!orderPlan),
  );

  hasOrdersToBeConfirmed$ = this.store.pipe(
    select(CartSelectors.selectCartLineCount),
    map((cartLineCount) => cartLineCount > 0),
  );

  customer$ = this.store.pipe(select(UserSelectors.selectCustomer), takeUntil(this.destroy$));
  location$ = this.store.pipe(select(UserSelectors.selectLocation), takeUntil(this.destroy$));

  showAddCropsButton$ = combineLatest([this.companySettings$, this.hasCropFields$]).pipe(
    map(([companySettings, hasCropFields]) => companySettings?.automaticAdvice && !hasCropFields),
  );

  hasIncompleteAdvicePlan$ = this.store.pipe(
    select(selectCropFieldAdvices),
    map((advices) => advices.filter((advice) => this.filterNonCropItemsOut(advice))),
    map((advices) => advices.some((advice) => (advice.treatments || []).length === 0)),
  );

  orderButtonOptions$ = combineLatest([
    this.hasOpenGbmOrders$,
    this.hasFertilizerProducts$,
    this.hasFertilizerPlanLoaded$,
    this.hasOrdersToBeConfirmed$,
  ]).pipe(
    map(([hasOpenGbmOrders, hasFertilizerProducts, hasFertilizerPlanLoaded, hasOrdersToBeConfirmed]) => {
      const options = [
        {
          text: this.locale.translate('report.cropcare'),
          action: this.boundOpenOrderGBMPopup,
          disabled: !hasOpenGbmOrders,
        },
        {
          text: this.locale.translate('report.openCart'),
          action: this.boundOpenShoppingCartAction,
          disabled: !hasOrdersToBeConfirmed,
        },
      ];

      if (hasFertilizerProducts || hasFertilizerPlanLoaded) {
        options.splice(1, 0, {
          text: this.locale.translate('report.fertilizers'),
          action: this.boundOpenOrderFertilizersPopup,
          disabled: !hasFertilizerProducts,
        });
      }

      return options;
    }),
  );

  hasAnyOrderOptionEnabled$ = this.orderButtonOptions$.pipe(
    map((options) => options.some((option) => !option.disabled)),
  );

  showOrderButton$ = combineLatest([this.hasCropFields$, this.companySettings$, this.hasAnyOrderOptionEnabled$]).pipe(
    map(
      ([hasCropFields, companySettings, hasAnyOrderOptionEnabled]) =>
        hasCropFields && companySettings?.automaticAdvice && hasAnyOrderOptionEnabled,
    ),
  );

  actions$: Observable<SummaryCardAction[]> = this.isUserEligibleToViewCropFieldAdvices$.pipe(
    map((isUserEligible) =>
      isUserEligible
        ? [
          {
            icon: 'print',
            callback: () => this.navigatePrintReport(),
            id: 'print',
          },
        ]
        : [
          // Hidden button so the customActions buttons isn't hudden
          {
            id: 'order',
          },
        ],
    ),
  );

  advices$!: Observable<CropFieldAdvice[]>;
  cropFields$!: Observable<CropField[]>;
  crops$!: Observable<RawCrop[]>;
  cropFieldsWrapper$!: Observable<CropFieldWrapper[]>;
  hasCornCropFields$!: Observable<boolean>;
  hasSugarBeetWarningVariety$!: Observable<boolean>;
  hasCichoreiWarningVariety$!: Observable<boolean>;
  warningInfo$!: Observable<Array<{ messageKey: string }>>

  ngOnInit() {
    this.store.dispatch(loadGenericCropAids());
    this.store.dispatch(loadCustomerOrderPlan());
    this.store.dispatch(loadGBMOrderLines({ shouldOpenOrderPlanSummary: false }));
    this.store.dispatch(loadFertilizerOrderLines({ shouldOpenOrderPlanSummary: false }));

    this.store.select(selectCropYear).pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(loadArableAdviceCropFields({ cropYear, cropFieldType: CropFieldType.Advice }));
      this.store.dispatch(loadProprietaryCropAids({ cropYear }));
      this.store.dispatch(loadAutomaticAdvice({ cropYear }));
      this.store.dispatch(loadCompanySettings({ year: cropYear }));
      this.store.dispatch(loadCropFieldAdvices({ cropYear }));
      this.store.dispatch(loadFertilizerOrderPlan({ year: cropYear }));
    });

    this.advices$ = this.store.select(selectCropFieldAdvices);
    this.cropFields$ = this.store.select(getArableAdviceCropFields);
    this.crops$ = this.store.select(getCrops);

    this.mapTableData();

    this.locale.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setupTableConfig();
    });
  }

  ngAfterViewInit() {
    // Setup table config after view is initialized and template ref is available
    this.setupTableConfig();
  }

  mapTableData() {
    this.cropFieldsWrapper$ = combineLatest([this.advices$, this.cropFields$, this.crops$]).pipe(
      map(([advices, cropFields, crops]) =>
        cropFields.map((cropField) => {
          const advice = advices.find((a) => a.cropFieldId === cropField.id);
          const treatments = advice?.treatments.map(
            (treatment) =>
              ({
                ...treatment,
                objective: this.locale.translate(treatment.objective),
              }) as Treatment,
          );

          const gbmTreatmentsFiltered = treatments?.filter((t) => t.treatmentType === TreatmentType.GBM) ?? [];
          const fertilizationTreatmentsFiltered = treatments?.filter((t) => t.treatmentType === TreatmentType.Fertilization) ?? [];

          const numberOfGbmTreatments = gbmTreatmentsFiltered.length;
          const numberOfFertilizationTreatments = fertilizationTreatmentsFiltered.length;
          return {
            cropField: {
              ...cropField,
              areaInHectares: Number((Math.round(cropField.areaInHectares * 100) / 100).toFixed(2)),
            },
            cropFieldName: `${cropField.name} (${cropField.areaInHectares} ha)`,
            crop: crops.find((crop) => crop.id === cropField.cropId),
            fertilizationTreatments: fertilizationTreatmentsFiltered,
            gbmTreatments: gbmTreatmentsFiltered,
            numberOfGbmTreatments,
            numberOfFertilizationTreatments,
            isAutomaticFertilizationAdvice: advice?.isAutomaticFertilizationAdvice,
            cropVarietyEdiCode: advice?.cropVariety?.ediCode,
          } as CropFieldWrapper;
        }),
      ),
    );

    // Initialize warning observables after cropFieldsWrapper$ is defined
    this.hasCornCropFields$ = this.cropFieldsWrapper$.pipe(
      map((cropFields) => cropFields.some((cf) => CORN_AGRIFIRM_CROP_IDS.includes(cf.crop?.agrifirmCrop?.id ?? 0))),
    );

    this.hasSugarBeetWarningVariety$ = this.cropFieldsWrapper$.pipe(
      map((cropFields) => {
        return cropFields.some(
          (cf) =>
            cf.crop?.agrifirmCrop?.id === SUIKERBIET_AGRIFIRM_CROP_ID &&
            cf.cropVarietyEdiCode !== undefined &&
            SUIKERBIET_WARNING_EDICROP_IDS.includes(cf.cropVarietyEdiCode),
        );
      }),
    );

    this.hasCichoreiWarningVariety$ = this.cropFieldsWrapper$.pipe(
      map((cropFields) => {
        return cropFields.some(
          (cf) =>
            cf.crop?.agrifirmCrop?.id === CICHOREI_AGRIFIRM_CROP_ID &&
            cf.cropVarietyEdiCode !== undefined &&
            CICHOREI_WARNING_EDICROP_IDS.includes(cf.cropVarietyEdiCode),
        );
      }),
    );

    this.warningInfo$ = combineLatest([
      this.companySettings$,
      this.hasCornCropFields$,
      this.hasSugarBeetWarningVariety$,
      this.hasCichoreiWarningVariety$,
    ]).pipe(
      map(([companySettings, hasCorn, hasSugarBeetWarning, hasCichoreiWarning]) => {
        if (!companySettings?.automaticAdvice) {
          return [];
        }

        const warnings: Array<{ messageKey: string }> = [];

        if (hasSugarBeetWarning) {
          warnings.push({ messageKey: 'warnings.sugarBeetConvisoSmart' });
        }
        if (hasCichoreiWarning) {
          warnings.push({ messageKey: 'warnings.chicoryWarning' });
        }
        if (hasCorn) {
          warnings.push({ messageKey: 'warnings.cornAdviceContact' });
        }

        return warnings;
      }),
    );
  }

  setupTableConfig() {
    combineLatest([
      this.cropFieldsWrapper$,
      this.crops$,
      this.store.select(this.greenManurePermission),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([cropFieldsWrapper, crops, canOpenGreenManure]) => {
        this.cropFieldsTableConfig.set({
          dataSource: new MatTableDataSource<CropFieldWrapper>(cropFieldsWrapper) as MatTableDataSource<unknown>,
          columns: [
            {
              id: 'cropFieldName',
              type: TableColumnTypes.STRING,
              label: this.locale.translate('cropField'),
              disableSort: true,
              className: 'flex__child--2 font-size-12',
              headerClassName: 'flex__child--2 text-center',
            },
            {
              id: 'crop.agrifirmCrop.name',
              type: TableColumnTypes.STRING,
              label: this.locale.translate('crop'),
              disableSort: true,
              className: 'flex__child--2 font-size-12',
              headerClassName: 'flex__child--2 text-center',
            },
            {
              id: 'gbmFertilizationTreatmentsCountText',
              type: TableColumnTypes.TEMPLATE,
              label: `${this.locale.translate('report.cropcare')} / ${this.locale.translate('report.fertilizers')}`,
              headerClassName: 'flex__child--3 text-center',
              className: 'flex__child--3 font-size-12',
              template: this.treatmentCellTemplate,
            },
            ...(this.environment.SHOW_GREEN_MANURE && canOpenGreenManure
              ? [
                  {
                    id: 'greenManureAction',
                    type: TableColumnTypes.TEMPLATE,
                    label: '',
                    disableSort: true,
                    template: this.greenManureActionTemplate,
                    className: 'green-manure-action-cell',
                    headerClassName: 'green-manure-action-cell',
                  },
                ]
              : []),
          ],
          actions: [
            {
              id: 'edit',
              callback: (cropFieldWrapper: unknown) => {
                this.openCropFieldDialog(cropFieldWrapper as CropFieldWrapper, crops);
              },
              label: '',
              icon: 'more_horiz',
              disabled: () => false,
              primary: false,
            },
          ],
          selectColumn: false,
          visibleCardColumns: 4,
          pagination: false,
          header: TableStickyTypes.STICKY,
          primaryColumn: ' ',
          hideActionLabelHeader: true,
        });
      });
  }

  openGreenManure(field: CropFieldWrapper): void {
    const customerId = this.selectedCustomer()?.id;
    if (!this.environment.SHOW_GREEN_MANURE || !this.canOpenGreenManure() || this.greenManureOpen() || !customerId) return;
    this.greenManureOpen.set(true);
    this.materialDialog
      .open<GreenManureComponent, GreenManureData>(GreenManureComponent, {
        data: { field, cropYear: this.cropYearService.cropYear(), customerId },
        panelClass: 'green-manure-dialog',
        width: 'var(--green-manure-dialog-width, 1280px)',
        maxWidth: 'var(--green-manure-dialog-max-width, calc(100vw - 32px))',
        height: 'var(--green-manure-dialog-height, min(960px, calc(100dvh - 32px)))',
        maxHeight: 'var(--green-manure-dialog-max-height, calc(100dvh - 32px))',
        closeOnNavigation: false,
        autoFocus: 'first-tabbable',
        ariaLabel: this.locale.translate('greenManure.title'),
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.greenManureOpen.set(false));
  }

  navigateAddCropsView() {
    this.router.navigate(['/crops']);
  }

  navigateShoppingCart() {
    const returnUrl = globalThis.location.href;

    combineLatest([this.customer$, this.location$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([customer, location]) => {
        this.store.dispatch(
          CartActions.openCartUrl({
            customerId: customer.id,
            locationId: location.id,
            returnUrl,
          }),
        );
      });
  }

  navigatePrintReport() {
    this.ai.trackEvent(NexusAIIdentifiers.clickPrint, {});
    this.router.navigate(['/report']);
  }

  openCropFieldDialog(cropFieldWrapper: CropFieldWrapper, crops: RawCrop[]) {
    this.ai.trackEvent(NexusAIIdentifiers.actionInTable, {
      cropField: cropFieldWrapper.cropField.id,
    });
    this.dialog.form(CropFieldDialogComponent, {
      cropField: cropFieldWrapper.cropField,
      crop: cropFieldWrapper.crop,
      title: cropFieldWrapper.cropField.name,
      fertilizationTreatments: cropFieldWrapper.fertilizationTreatments,
      gbmTreatments: cropFieldWrapper.gbmTreatments,
      isAutomaticFertilizationAdvice: cropFieldWrapper.isAutomaticFertilizationAdvice,
      crops,
      fullscreen: true,
      confirmButton: false,
      pushHistoryState: false,
      cancelButtonText: this.locale.translate('close'),
    });
  }

  orderGbm(): void {
    this.ai.trackEvent(NexusAIIdentifiers.order, {});
    this.store.dispatch(editCustomerProductStock());
  }

  orderFertilizers(): void {
    this.ai.trackEvent(NexusAIIdentifiers.order, {});
    this.store.dispatch(openFertilizerProposalDialog());
  }

  filterNonCropItemsOut(advice: CropFieldAdvice): boolean {
    const nonCropItems = [1015, 1025, 1026, 1195];
    return !nonCropItems.includes(advice.agrifirmCropId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
